if (!customElements.get("product-inventory")) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.section = this.closest(".js-product");
      this.inventoryNotice = this.querySelector(".js-inventory-notice");
      this.urgencyMessage = this.querySelector(".js-inventory-urgency");
      this.indicatorBar = this.querySelector(".js-inventory-indicator");
      this.quantityInput = this.section.querySelector(
        '.js-product-form-main input[name="quantity"]',
      );
      this.addButton = this.section.querySelector(
        '.js-product-form-main [name="add"]',
      );

      this.section.addEventListener(
        "on:variant:change",
        this.handleVariantChange.bind(this),
      );
      for (const eventName of ["input", "change"]) {
        this.section.addEventListener(eventName, (event) => {
          if (
            event.target.matches('.js-product-form-main input[name="quantity"]')
          ) {
            this.updateDeliveryText();
          }
        });
      }

      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === "true",
        this.dataset.inventoryPolicy,
        this.dataset.inventoryManaged === "true",
        parseInt(this.dataset.supplierInventoryQuantity, 10) || 0,
        this.dataset.supplierAvailabilityStatus,
        this.dataset.deliveryDays,
      );
      this.deliveryTimer = setInterval(() => this.updateDeliveryText(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.deliveryTimer);
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.getVariantInventory();
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          inventory.inventory_managed,
          inventory.supplier_inventory_quantity,
          inventory.supplier_availability_status,
          inventory.delivery_days,
        );
      } else {
        this.updateInventory(0, false, "deny", true, 0, "out_of_stock", "");
        this.hidden = true;
      }
    }

    updateDeliveryText() {
      if (!this.inventoryState || !window.MilitarshopDelivery) return;
      this.quantityInput = this.section.querySelector(
        '.js-product-form-main input[name="quantity"]',
      );
      const variant = this.getVariantInventory();
      window.MilitarshopDelivery.render(this.inventoryNotice, {
        local: this.inventoryState.localCount,
        supplier: this.inventoryState.supplierCount,
        status: variant.supplier_availability_status || "out_of_stock",
        restockDate: variant.supplier_expected_receipt_date,
        managed: this.inventoryState.inventoryManaged,
        available: variant.available,
        days: this.inventoryState.deliveryDays,
        maxDays: variant.delivery_days_max,
        quantity: Math.max(parseInt(this.quantityInput?.value, 10) || 1, 1),
      });
    }

    updatePurchaseLimits(count, inventoryManaged, backorder = false) {
      if (!inventoryManaged) return;

      this.quantityInput = this.section.querySelector(
        '.js-product-form-main input[name="quantity"]',
      );
      this.addButton = this.section.querySelector(
        '.js-product-form-main [name="add"]',
      );

      if (this.quantityInput) {
        if (backorder) this.quantityInput.removeAttribute("max");
        else this.quantityInput.max = String(Math.max(count, 1));
        this.quantityInput.disabled = count <= 0 && !backorder;
        if (!backorder && count > 0 && parseInt(this.quantityInput.value, 10) > count) {
          this.quantityInput.value = String(count);
        }
      }

      if (!this.addButton) return;
      if (count <= 0 && !backorder) {
        this.addButton.dataset.inventoryDisabled = "true";
        this.addButton.disabled = true;
        this.addButton.textContent = theme.strings.noStock;
      } else if (this.addButton.dataset.inventoryDisabled === "true") {
        delete this.addButton.dataset.inventoryDisabled;
        this.addButton.disabled = false;
        this.addButton.textContent = this.addButton.dataset.addToCartText;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(
      count,
      available,
      inventoryPolicy,
      inventoryManaged,
      supplierCount,
      supplierStatus,
      deliveryDays,
    ) {
      const localCount = Math.max(parseInt(count, 10) || 0, 0);
      const sellableSupplierCount =
        supplierStatus === "available"
          ? Math.max(parseInt(supplierCount, 10) || 0, 0)
          : 0;
      const sellableCount = localCount + sellableSupplierCount;
      const backorder = supplierStatus === "backorder" && available && inventoryPolicy === "continue" && Number(deliveryDays) > 0;
      let inventoryLevel;

      if (sellableCount <= 0) {
        if (backorder) {
          inventoryLevel = "backordered";
        } else if (inventoryManaged) {
          inventoryLevel = "none";
        } else if (inventoryPolicy === "continue") {
          inventoryLevel = "backordered";
        } else if (available) {
          inventoryLevel = "in_stock";
        } else {
          inventoryLevel = "none";
        }
      } else if (sellableCount <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = "very_low";
      } else if (sellableCount <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = "low";
      } else {
        inventoryLevel = "normal";
      }

      this.inventoryState = {
        count: sellableCount,
        backorder,
        localCount,
        inventoryManaged,
        supplierCount: sellableSupplierCount,
        deliveryDays: deliveryDays ? String(deliveryDays) : "",
      };
      this.updatePurchaseLimits(sellableCount, inventoryManaged, backorder);

      if (
        ((this.dataset.showNotice === "always" ||
          (this.dataset.showNotice === "low" &&
            inventoryLevel.includes("low"))) &&
          !(
            inventoryLevel === "backordered" &&
            this.dataset.showNoStockBackordered === "false"
          )) ||
        inventoryManaged
      ) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute("data-inventory-level", inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryManaged) {
          this.updateDeliveryText();
        } else if (inventoryLevel === "backordered") {
          this.inventoryNotice.innerText = theme.strings.backordered;
        } else if (
          inventoryLevel !== "in_stock" &&
          (this.dataset.showCount === "always" ||
            (this.dataset.showCount === "low" &&
              inventoryLevel.includes("low")))
        ) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace(
            "[quantity]",
            sellableCount,
          );
        } else if (inventoryLevel === "very_low") {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === "low") {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (
          inventoryLevel === "normal" ||
          inventoryLevel === "in_stock"
        ) {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === "none") {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage && !inventoryManaged) {
          if (inventoryLevel === "very_low") {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === "low") {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === "backordered") {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (
            inventoryLevel === "normal" ||
            inventoryLevel === "in_stock"
          ) {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === "none") {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar && !inventoryManaged) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if (
            (sellableCount >= this.dataset.scale ||
              inventoryLevel === "in_stock") &&
            inventoryLevel !== "backordered"
          ) {
            newWidth = 100;
          } else {
            newWidth = (
              (100 / parseInt(this.dataset.scale, 10)) *
              sellableCount
            ).toFixed(1);
          }
          this.indicatorBar.querySelector("span").style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define("product-inventory", ProductInventory);
}
